import type { NewThemeFields } from '@tribeplatform/gql-client/graphql'
import type {
  AuthToken,
  MutationUpsertThemeArgs,
  Network,
  NewTheme,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'
import {
  useCachedNetwork,
  networkUpdater,
  authTokenUpdater,
} from '../cache/useCachedNetwork.js'

export const useUpsertTheme = (options?: {
  fields?: NewThemeFields
  useMutationOptions?: UseMutationOptions<
    NewTheme,
    Error,
    MutationUpsertThemeArgs,
    { snapshot: Network }
  >
}) => {
  const { useMutationOptions, fields } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const authTokenKey = getAuthTokenKey()
  const networkSnapshot = useCachedNetwork()

  return useMutation<
    NewTheme,
    Error,
    MutationUpsertThemeArgs,
    { snapshot: Network }
  >((input: MutationUpsertThemeArgs) => client.theme.upsert(input, fields), {
    ...useMutationOptions,
    onMutate: async newTheme => {
      const snapshot = networkSnapshot

      if (newTheme.input?.active) {
        await queryClient.cancelQueries(networkKey)

        queryClient.setQueriesData<Network>(
          networkKey,
          networkUpdater({ activeTheme: newTheme.input }),
        )
        queryClient.setQueriesData<AuthToken>(
          authTokenKey,
          authTokenUpdater({
            network: { activeTheme: newTheme.input },
          }),
        )
      }

      useMutationOptions?.onMutate?.(newTheme)
      return { snapshot }
    },
    onSuccess: (newTheme, variables, context) => {
      queryClient.setQueriesData<AuthToken>(
        authTokenKey,
        authTokenUpdater({
          network: { activeTheme: newTheme },
        }),
      )
      useMutationOptions?.onSuccess?.(newTheme, variables, context)
    },
    onError: (err, newTheme, context) => {
      queryClient.setQueriesData<Network>(networkKey, context.snapshot)
      queryClient.setQueriesData<AuthToken>(
        authTokenKey,
        authTokenUpdater({ network: context.snapshot }),
      )
      useMutationOptions?.onError?.(err, newTheme, context)
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries(networkKey)
      queryClient.invalidateQueries(authTokenKey)
      useMutationOptions?.onSettled?.(data, error, variables, context)
    },
  })
}
